import { Component } from '@theme/component'
import { CartAddEvent, CartErrorEvent, ThemeEvents } from '@theme/events'
import { fetchConfig, readCssDurationMs } from '@theme/maths'
import { morph } from '@theme/morph'
import { cartPerformance } from '@theme/performance'

export const ProductFormConfig = {
  animationDuration: 2000,
  classes: {
    addedText: '.add-to-cart-text--added',
    hidden: 'hidden'
  },
  cleanupTimeout: 10,
  durationToken: '--duration',
  errorTimeout: 10000,
  successTimeout: 5000
}

export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton']

  #animationTimeout

  #cleanupTimeout

  #feedbackDuration = ProductFormConfig.animationDuration

  #feedbackStartedAt = 0

  disconnectedCallback() {
    super.disconnectedCallback()
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout)
    }
    if (this.#cleanupTimeout) {
      clearTimeout(this.#cleanupTimeout)
    }
    this.#feedbackStartedAt = 0
  }

  /**
   * @returns {boolean} True while the add to cart animation still owns the loading class
   */
  get ownsFeedback() {
    return this.#feedbackStartedAt > 0
  }

  /**
   * @returns {number} Milliseconds left before the add to cart animation ends
   */
  get feedbackDelayRemaining() {
    if (!this.#feedbackStartedAt) {
      return 0
    }
    const elapsed = Date.now() - this.#feedbackStartedAt
    return Math.max(0, this.#feedbackDuration - elapsed)
  }

  disable() {
    this.refs.addToCartButton.disabled = true
  }

  enable() {
    this.refs.addToCartButton.disabled = false
  }

  handleClick() {
    const form = this.closest('form')
    if (!form?.checkValidity()) {
      return
    }
    this.#feedbackDuration = readCssDurationMs(
      this.refs.addToCartButton,
      ProductFormConfig.durationToken,
      ProductFormConfig.animationDuration
    )
    this.#feedbackStartedAt = Date.now()
    this.animateAddToCart()

    const customCursor = document.querySelector('custom-cursor')
    customCursor?.setLoading?.(this.#feedbackDuration, { minimum: 0 })
  }

  animateAddToCart() {
    const { addToCartButton } = this.refs
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout)
    }
    if (this.#cleanupTimeout) {
      clearTimeout(this.#cleanupTimeout)
    }
    addToCartButton.classList.add('is-loading')
    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.#feedbackStartedAt = 0
        this.refs.addToCartButton.classList.remove('is-loading')
      }, ProductFormConfig.cleanupTimeout)
    }, this.#feedbackDuration)
  }
}

if (!customElements.get('add-to-cart')) {
  customElements.define('add-to-cart', AddToCartComponent)
}

export class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion']
  #abortController = new AbortController()

  #timeout

  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return
    }
    const { variantId, addToCartButtonContainer } = this.refs
    const currentAddToCartButton =
      addToCartButtonContainer?.refs.addToCartButton
    const newAddToCartButton = event.detail.data.html.querySelector(
      '[ref="addToCartButton"]'
    )
    if (!currentAddToCartButton) {
      return
    }
    if (
      event.detail.resource == null ||
      event.detail.resource.available === false
    ) {
      addToCartButtonContainer.disable()
    } else {
      addToCartButtonContainer.enable()
    }
    if (newAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton)
    }
    variantId.value = event.detail.resource.id ?? ''
    if (event.detail.resource) {
      const productVariantMedia =
        event.detail.resource.featured_media?.preview_image?.src
      if (productVariantMedia) {
        addToCartButtonContainer?.setAttribute(
          'data-product-variant-media',
          `${productVariantMedia}&width=100`
        )
      }
    }
  }

  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable()
  }

  connectedCallback() {
    super.connectedCallback()
    const { signal } = this.#abortController
    const target = this.closest('.shopify-section, dialog, product-card')
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, {
      signal
    })
    target?.addEventListener(
      ThemeEvents.variantSelected,
      this.#onVariantSelected,
      { signal }
    )
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.#abortController.abort()
  }

  handleSubmit(event) {
    const { addToCartTextError } = this.refs
    event.preventDefault()
    if (this.#timeout) {
      clearTimeout(this.#timeout)
    }
    if (
      this.refs.addToCartButtonContainer?.refs.addToCartButton?.getAttribute(
        'disabled'
      ) === 'true'
    ) {
      return
    }
    const form = this.querySelector('form')
    if (!form) {
      throw new Error('Product form element missing')
    }
    const formData = new FormData(form)
    const requestedQuantityRaw = Number(
      formData.get('quantity') ?? this.dataset.quantityDefault ?? 0
    )
    const requestedQuantity = Number.isNaN(requestedQuantityRaw)
      ? 0
      : requestedQuantityRaw
    const cartItemsComponents = document.querySelectorAll('cart-items')
    const cartItemComponentsSectionIds = []
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId)
      }
    })
    formData.append('sections', cartItemComponentsSectionIds.join(','))
    const addToCartButton =
      this.refs.addToCartButtonContainer?.refs?.addToCartButton
    addToCartButton?.classList?.add('is-loading')
    const fetchCfg = fetchConfig('javascript', { body: formData })
    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html'
      }
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(
              form.getAttribute('id') || '',
              response.message,
              response.description,
              response.errors
            )
          )
          this.#showAddToCartError(response.message)
          const itemCount = this.#getCurrentItemCount()
          this.dispatchEvent(
            new CartAddEvent(response, this.id, {
              animationDuration: this.#feedbackDelay(),
              didError: true,
              itemCount,
              productId: this.dataset.productId,
              source: 'product-form'
            })
          )
          return
        }
        const id = formData.get('id')
        addToCartTextError?.classList.add(ProductFormConfig.classes.hidden)
        if (!id) {
          throw new Error('Form ID is required')
        }
        if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
          const submittedButton =
            this.refs.addToCartButtonContainer.refs.addToCartButton
          const addedTextElement = submittedButton.querySelector(
            ProductFormConfig.classes.addedText
          )
          const addedText =
            addedTextElement?.textContent?.trim() || Theme.translations.added
          this.#setLiveRegionText(addedText)
          setTimeout(() => {
            this.#clearLiveRegionText()
          }, ProductFormConfig.successTimeout)
        }
        const itemCount = this.#resolveItemCount(response, requestedQuantity)
        this.dispatchEvent(
          new CartAddEvent(response, id.toString(), {
            animationDuration: this.#feedbackDelay(),
            itemCount,
            productId: this.dataset.productId,
            sections: response.sections,
            source: 'product-form'
          })
        )
      })
      .catch(() => {
        this.#showAddToCartError(Theme.translations.cartError)
      })
      .finally(() => {
        const container = this.refs.addToCartButtonContainer
        if (!container?.ownsFeedback) {
          container?.refs?.addToCartButton?.classList?.remove('is-loading')
        }
        cartPerformance.measureFromEvent('add:user-action', event)
      })
  }

  #feedbackDelay() {
    return this.refs.addToCartButtonContainer?.feedbackDelayRemaining ?? 0
  }

  #showAddToCartError(message) {
    const { addToCartTextError } = this.refs
    addToCartTextError?.classList.remove(ProductFormConfig.classes.hidden)
    if (addToCartTextError) {
      addToCartTextError.textContent = message
    }
    this.#setLiveRegionText(message)
    this.#timeout = setTimeout(() => {
      this.refs.addToCartTextError?.classList.add(
        ProductFormConfig.classes.hidden
      )
      this.#clearLiveRegionText()
    }, ProductFormConfig.errorTimeout)
  }

  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion
    liveRegion.textContent = text
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion
    liveRegion.textContent = ''
  }

  #resolveItemCount(response, requestedQuantity) {
    if (response && typeof response.item_count === 'number') {
      return response.item_count
    }

    const sectionCount = this.#getItemCountFromSections(response?.sections)
    if (typeof sectionCount === 'number') {
      return sectionCount
    }

    const currentCount = this.#getCurrentItemCount()
    return currentCount + requestedQuantity
  }

  #getItemCountFromSections(sections) {
    if (!sections || typeof sections !== 'object') {
      return null
    }

    for (const sectionId in sections) {
      if (!Object.hasOwn(sections, sectionId)) {
        continue
      }
      const html = sections[sectionId]
      if (typeof html !== 'string') {
        continue
      }
      const parsedHtml = new DOMParser().parseFromString(html, 'text/html')
      const countEl = parsedHtml.querySelector('[ref="cartItemCount"]')
      if (!countEl) {
        continue
      }
      const count = Number.parseInt(countEl.textContent ?? '', 10)
      if (!Number.isNaN(count)) {
        return count
      }
    }

    return null
  }

  #getCurrentItemCount() {
    const countEl = document.querySelector('[ref="cartItemCount"]')
    if (countEl) {
      const count = Number.parseInt(countEl.textContent ?? '', 10)
      if (!Number.isNaN(count)) {
        return count
      }
    }

    try {
      const stored = sessionStorage.getItem('cart-count')
      if (stored) {
        const { value } = JSON.parse(stored)
        const count = Number.parseInt(value, 10)
        if (!Number.isNaN(count)) {
          return count
        }
      }
    } catch {}

    return 0
  }
}

if (!customElements.get('product-form')) {
  customElements.define('product-form', ProductFormComponent)
}
